import { Link, useLocation } from '@tanstack/react-router'

import { AvatarMenu } from '@/features/nav/avatar-menu'
import { LanguageMenu, ThemeMenu } from '@/features/nav/preference-menus'
import { NotificationBell } from '@/features/notifications/bell'
import type { AuthState, System } from '@/lib/auth-store'
import { useT } from '@/lib/i18n/translate'
import { loginPathFor, loginSearchFor } from '@/lib/return-path'

/**
 * The right side of every header: language, theme, then the bell and account
 * menu for a logged-in user or Log in (with return path) for anyone else.
 */
export function HeaderActions<S extends System>({ system, auth }: { system: S; auth: AuthState<S> }) {
  const t = useT()
  const location = useLocation()
  const authenticated = auth.status === 'authenticated'
  return (
    <div className="flex shrink-0 items-center gap-1">
      <LanguageMenu system={authenticated ? system : undefined} />
      <ThemeMenu />
      {authenticated ? (
        <>
          <NotificationBell system={system} />
          <AvatarMenu system={system} user={auth.user} />
        </>
      ) : (
        auth.status === 'anonymous' && (
          <Link
            to={loginPathFor(system)}
            search={loginSearchFor(system, location)}
            className="text-accent-ink hover:bg-accent-soft flex min-h-tap items-center rounded-lg px-3 font-medium"
          >
            {t('auth.login')}
          </Link>
        )
      )}
    </div>
  )
}
